/**
 * Strategy Blueprint Mock
 *
 * Architecture diagram showing the five core components of a
 * trading strategy and how they flow into the complete system.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCrosshairs,
  faDoorOpen,
  faShieldHalved,
  faFilter,
  faScaleBalanced,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface Component {
  label: string;
  icon: IconDefinition;
  color: string;
  desc: string;
}

const components: Component[] = [
  { label: 'Entry', icon: faCrosshairs, color: '#3b82f6', desc: 'When to open a trade' },
  { label: 'Exit', icon: faDoorOpen, color: '#8b5cf6', desc: 'When to close a trade' },
  { label: 'Risk Mgmt', icon: faShieldHalved, color: '#ef4444', desc: 'How much to lose at most' },
  { label: 'Filters', icon: faFilter, color: '#f59e0b', desc: 'When not to trade' },
  { label: 'Sizing', icon: faScaleBalanced, color: '#22c55e', desc: 'How large each position' },
];

function ComponentBlock({ comp }: { comp: Component }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '0.625rem 0.5rem', background: '#181825',
      border: `1px solid ${comp.color}30`, borderRadius: 8,
      minWidth: 90, flex: 1,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: `${comp.color}18`, display: 'flex',
        alignItems: 'center', justifyContent: 'center', marginBottom: '0.35rem',
      }}>
        <FontAwesomeIcon icon={comp.icon} style={{ fontSize: '0.65rem', color: comp.color }} />
      </div>
      <div style={{
        fontSize: '0.6875rem', fontWeight: 600, color: '#e5e7eb',
        marginBottom: '0.15rem', textAlign: 'center',
      }}>
        {comp.label}
      </div>
      <div style={{
        fontSize: '0.5625rem', color: '#6b7280', textAlign: 'center',
      }}>
        {comp.desc}
      </div>
    </div>
  );
}

export default function StrategyBlueprintMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Strategy Architecture
      </div>
      <div className="platform-mock-body" style={{ padding: '1rem' }}>
        {/* Component blocks in a flex row */}
        <div style={{
          display: 'flex', gap: '0.5rem', flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          {components.map((c) => (
            <ComponentBlock key={c.label} comp={c} />
          ))}
        </div>

        {/* Arrow down */}
        <div style={{
          display: 'flex', justifyContent: 'center',
          padding: '0.5rem 0', color: '#4b5563', fontSize: '0.875rem',
        }}>
          <FontAwesomeIcon icon={faArrowRight} style={{ transform: 'rotate(90deg)' }} />
        </div>

        {/* Combined output */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '0.5rem', padding: '0.625rem 1rem',
          background: 'rgba(59,130,246,0.06)',
          border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: 8,
        }}>
          <div style={{
            fontSize: '0.75rem', fontWeight: 700, color: '#93c5fd',
            letterSpacing: '0.03em',
          }}>
            Complete Strategy System
          </div>
          <div style={{ fontSize: '0.5625rem', color: '#6b7280' }}>
            &mdash; testable, repeatable, scalable
          </div>
        </div>

        <div style={{
          marginTop: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)',
          borderRadius: 6, fontSize: '0.6875rem', color: '#9ca3af',
        }}>
          <span style={{ color: '#3b82f6', fontWeight: 600 }}>Key: </span>
          Remove any one component and the system becomes incomplete.
          All five must be defined for a strategy to be backtestable.
        </div>
      </div>
      <div className="platform-mock-caption">
        A strategy is not one rule — it is an architecture of five interacting components.
      </div>
    </div>
  );
}
